import {HTMLReactParserOptions, Element as ParserElement, DOMNode, domToReact} from 'html-react-parser';
import {Fragment, useCallback, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {Button, NodeElement, ClientDynamicContent, Icon, UserPreview} from 'components';
import {DynamicComponentNames, AGENTS_UNAVAILABLE_DEFAULT_TEMPLATE, AgentsUnavailableUnits as Units} from 'constants/client';
import {useClientSession} from 'hooks';
import {ClientDynamicLayout} from 'layouts';
import {TRANSLATION_KEYS} from 'locale/translation-keys';
import {dynamicContentUtils} from 'utils';
import {getParticipantName} from 'utils/transform';
import './styles.scss';

import {RequestAppointmentView} from '../RequestAppointmentView';

const T_KEYS = TRANSLATION_KEYS.CLIENT_IN_SESSION_PAGE.IN_SESSION_WEB.AGENTS_UNAVAILABLE;
const T_KEYS_COMMON = TRANSLATION_KEYS.CLIENT_IN_SESSION_PAGE;

const {isMatchElement, nodeElementParent, renderDefaultNode} = dynamicContentUtils;
const getOptions = (props: any): HTMLReactParserOptions => {
    const {
        description, subDescription, setupBtnLabel, onSetupClick, requestAppointmentEnabled, agentName, yourAgentLabel,
        localParticipant
    } = props;
    return {
        replace: (domNode: any) => {
            if (!domNode.attribs) {
                return null;
            }

            const node = domNode as ParserElement;
            const showAgentData = !!agentName;

            if (isMatchElement(Units.AgentInfo, node)) {
                return nodeElementParent({show: showAgentData});
            }
            if (isMatchElement(Units.YourAgentLabel, node)) {
                return <NodeElement show={showAgentData} node={node}>{yourAgentLabel}</NodeElement>;
            }
            if (isMatchElement(Units.AgentPreview, node)) {
                return <NodeElement show={showAgentData} node={node}><UserPreview name={agentName} /></NodeElement>;
            }
            if (isMatchElement(Units.AgentName, node)) {
                return <NodeElement show={showAgentData} node={node}>{agentName}</NodeElement>;
            }

            if (isMatchElement(Units.Icon, node)) {
                return (
                    <NodeElement node={node}>
                        <Icon name='agent-unavailable' size='8xl' color='default-dimmed' />
                    </NodeElement>);
            }
            if (isMatchElement(Units.Description, node)) {
                return <NodeElement node={node}>{description}</NodeElement>;
            }
            if (isMatchElement(Units.SubDescription, node)) {
                return <NodeElement node={node}>{subDescription}</NodeElement>;
            }
            if (isMatchElement(Units.SetupButton, node)) {
                return (
                    <NodeElement node={node} show={requestAppointmentEnabled} onClick={onSetupClick}>
                        {domToReact(node.children as DOMNode[])}
                    </NodeElement>
                );
            }
            if (isMatchElement(Units.SetupButtonLabel, node)) {
                return <NodeElement node={node} show={requestAppointmentEnabled}>{setupBtnLabel}</NodeElement>;
            }
            if (isMatchElement(Units.SetupButtonDefault, node)) {
                return (
                    <NodeElement node={node} show={requestAppointmentEnabled}>
                        <Button
                            color='default'
                            width='small'
                            rounded
                            onClick={onSetupClick}
                        >
                            <span className='tw-uppercase'>{setupBtnLabel}</span>
                        </Button>
                    </NodeElement>
                );
            }
            return renderDefaultNode(node, {role: localParticipant.role});
        }
    };
};

export const AgentsUnavailableView: React.FC = () => {
    const {t} = useTranslation();
    const {image, requestAppointmentEnabled, agent} = useClientSession();
    const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
    const description = useMemo(() => t(T_KEYS.DESCRIPTION), [t]);
    const subDescription = useMemo(() => t(requestAppointmentEnabled ? T_KEYS.CALL_OR_MESSAGE_LATER : T_KEYS.CALL_LATER),
        [t, requestAppointmentEnabled]);
    const setupBtnLabel = useMemo(() => t(T_KEYS_COMMON.SET_UP_CALL), [t]);
    const yourAgentLabel = useMemo(() => t(T_KEYS_COMMON.YOUR_AGENT_IS), [t]);
    const agentName = useMemo(() => getParticipantName(agent?.data), [agent]);
    const onSetupClick = useCallback(() => setIsFormOpen(true), [setIsFormOpen]);
    return (
        <Fragment>
            {!isFormOpen &&
                <ClientDynamicLayout image={image}>
                    <ClientDynamicContent
                        component={DynamicComponentNames.AgentsUnavailable}
                        defaultTemplate={AGENTS_UNAVAILABLE_DEFAULT_TEMPLATE}
                        optionsProps={{
                            requestAppointmentEnabled, description, subDescription, setupBtnLabel, agentName, yourAgentLabel,
                            onSetupClick
                        }}
                        optionsGetter={getOptions}
                    />
                </ClientDynamicLayout>}
            {requestAppointmentEnabled && isFormOpen &&
                <RequestAppointmentView onCloseForm={() => setIsFormOpen(false)} />}
        </Fragment>

    );
};
